import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { B2BAPI } from '../lib/b2bApi';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  FlaskConical, Pill, Package, Search, ShoppingCart, Plus, Minus,
  IndianRupee, Clock, Building2, Home, X, Download, FileText,
  Filter, BarChart3, Edit2, MessageCircle, User, Phone, Mail, Tag,
  CheckCircle, AlertCircle, Loader2, Users, Wallet, Lock, Trash2
} from 'lucide-react';
import { MobileTabNav } from '../components/ui/mobile-tab-nav';
import DoctorLinkVendors from './DoctorLinkVendors';
import AddressPickerCheckout from './AddressPickerCheckout';
import { InvoiceUploader, ReviewSection } from '../components/ReviewsAndInvoices';

export default function MarketplacePage() {
  const [activeTab, setActiveTab] = useState('lab-tests');
  const [labTests, setLabTests] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reports, setReports] = useState([]);
  const [labs, setLabs] = useState([]);
  const [patients, setPatients] = useState([]);
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('marketplace_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showPatientSelect, setShowPatientSelect] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [placingOrder, setPlacingOrder] = useState(false);
  
  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('marketplace_cart', JSON.stringify(cart));
  }, [cart]);
  
  // Order filters
  const [orderFilters, setOrderFilters] = useState({
    vendor_type: 'all', status: 'all', search: '', date_from: '', date_to: ''
  });
  
  // Report filters
  const [reportFilters, setReportFilters] = useState({
    patient_name: '', vendor_id: 'all', date_from: '', date_to: ''
  });
  
  // Order details with discount and GST
  const [orderDetails, setOrderDetails] = useState({
    patient_id: '', patient_name: '', patient_phone: '', patient_email: '',
    patient_age: '', patient_gender: '', delivery_address: '', delivery_phone: '',
    delivery_location: null, notes: '',
    discount_type: 'none', discount_value: 0, gst_percent: 18, apply_gst: true,
    payment_method: 'wallet'
  });
  
  // Available coupons
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  
  // Applied coupons per vendor (vendor_id -> coupon)
  const [appliedCoupons, setAppliedCoupons] = useState({});
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  
  // Photo lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // My Vendors state
  const [myVendors, setMyVendors] = useState([]);
  const [pendingVendorRequests, setPendingVendorRequests] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [showRequestVendorDialog, setShowRequestVendorDialog] = useState(false);
  const [requestVendorNotes, setRequestVendorNotes] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [requestingVendor, setRequestingVendor] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [labRes, medRes, invRes, ordersRes, reportsRes, patientsRes, couponsRes, walletRes] = await Promise.all([
        B2BAPI.get('/lab-tests'),
        B2BAPI.get('/medicines'),
        B2BAPI.get('/inventory'),
        B2BAPI.get('/orders/my-orders'),
        B2BAPI.get('/reports/clinic-reports').catch(() => ({ data: { reports: [], labs: [] } })),
        API.get('/patients'),
        B2BAPI.get('/coupons/all-active').catch(() => ({ data: [] })),
        API.get('/wallet/balance').catch(() => ({ data: { balance: 0 } })),
      ]);
      setLabTests(labRes.data);
      setMedicines(medRes.data);
      setInventory(invRes.data);
      setOrders(ordersRes.data);
      setReports(reportsRes.data.reports || []);
      setLabs(reportsRes.data.labs || []);
      setPatients(patientsRes.data || []);
      setAvailableCoupons(couponsRes.data || []);
      setWalletBalance(walletRes.data?.balance || 0);
    } catch (err) {
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (orderFilters.vendor_type && orderFilters.vendor_type !== 'all') params.append('vendor_type', orderFilters.vendor_type);
      if (orderFilters.status && orderFilters.status !== 'all') params.append('status', orderFilters.status);
      if (orderFilters.search) params.append('search', orderFilters.search);
      if (orderFilters.date_from) params.append('date_from', orderFilters.date_from);
      if (orderFilters.date_to) params.append('date_to', orderFilters.date_to);
      
      const res = await B2BAPI.get(`/orders/my-orders?${params.toString()}`);
      setOrders(res.data);
    } catch (err) {
      toast.error('Failed to filter orders');
    }
  };

  const fetchFilteredReports = async () => {
    try {
      const params = new URLSearchParams();
      if (reportFilters.patient_name) params.append('patient_name', reportFilters.patient_name);
      if (reportFilters.vendor_id && reportFilters.vendor_id !== 'all') params.append('vendor_id', reportFilters.vendor_id);
      if (reportFilters.date_from) params.append('date_from', reportFilters.date_from);
      if (reportFilters.date_to) params.append('date_to', reportFilters.date_to);
      
      const res = await B2BAPI.get(`/reports/clinic-reports?${params.toString()}`);
      setReports(res.data.reports || []);
    } catch (err) {
      toast.error('Failed to filter reports');
    }
  };

  // My Vendors Functions
  const fetchMyVendors = async () => {
    setVendorsLoading(true);
    try {
      const token = localStorage.getItem('clinic_token');
      const [vendorsRes, pendingRes, availableRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/doctor/my-vendors`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/doctor/pending-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/doctor/available-vendors`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMyVendors(vendorsRes.data || []);
      setPendingVendorRequests(pendingRes.data || []);
      setAvailableVendors(availableRes.data || []);
    } catch (err) {
      console.error('Failed to fetch my vendors', err);
      toast.error('Failed to load vendor connections');
    } finally {
      setVendorsLoading(false);
    }
  };

  const requestVendorConnection = async () => {
    if (!selectedVendorId) {
      toast.error('Please select a vendor');
      return;
    }
    setRequestingVendor(true);
    try {
      const token = localStorage.getItem('clinic_token');
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/doctor/request-vendor`, 
        { target_id: selectedVendorId, notes: requestVendorNotes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Connection request sent for approval');
      setShowRequestVendorDialog(false);
      setSelectedVendorId('');
      setRequestVendorNotes('');
      fetchMyVendors();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send request');
      toast.error(errorMsg);
    } finally {
      setRequestingVendor(false);
    }
  };

  const getVendorIcon = (type) => {
    switch (type) {
      case 'lab': return <FlaskConical className="w-5 h-5 text-cyan-600" />;
      case 'pharma': return <Pill className="w-5 h-5 text-pink-600" />;
      case 'inventory': return <Package className="w-5 h-5 text-orange-600" />;
      default: return <Building2 className="w-5 h-5 text-slate-600" />;
    }
  };

  // Group items by product for price comparison
  const groupByProduct = (items, type) => {
    const grouped = {};
    items.forEach(item => {
      const key = type === 'lab' ? item.name : (item.generic_name || item.name);
      if (!grouped[key]) {
        grouped[key] = { name: key, vendors: [] };
      }
      grouped[key].vendors.push(item);
    });
    // Sort each group by b2b_price
    Object.values(grouped).forEach(g => {
      g.vendors.sort((a, b) => a.b2b_price - b.b2b_price);
    });
    return Object.values(grouped);
  };

  // Calculate price based on quantity tiers
  // price_tiers format: {"1-10": 100, "11-50": 90, "51+": 80} or {"1": 100, "5": 95, "10": 90}
  const calculateTieredPrice = (basePice, quantity, priceTiers) => {
    if (!priceTiers || Object.keys(priceTiers).length === 0) {
      return basePice;
    }
    
    // Sort tiers by quantity (extract min value from range)
    const sortedTiers = Object.entries(priceTiers)
      .map(([range, price]) => {
        let minQty;
        if (range.includes('-')) {
          minQty = parseInt(range.split('-')[0]);
        } else if (range.endsWith('+')) {
          minQty = parseInt(range.replace('+', ''));
        } else {
          minQty = parseInt(range);
        }
        return { range, minQty, price };
      })
      .sort((a, b) => b.minQty - a.minQty); // Sort descending
    
    // Find the applicable tier
    for (const tier of sortedTiers) {
      if (quantity >= tier.minQty) {
        return tier.price;
      }
    }
    
    return basePice;
  };

  const addToCart = (item, type) => {
    const existingIndex = cart.findIndex(c => c.item_id === item.id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      const newQty = newCart[existingIndex].quantity + 1;
      const tieredPrice = calculateTieredPrice(item.b2b_price, newQty, item.price_tiers);
      newCart[existingIndex].quantity = newQty;
      newCart[existingIndex].unit_price = tieredPrice;
      newCart[existingIndex].total_price = newQty * tieredPrice;
      newCart[existingIndex].price_tiers = item.price_tiers;
      newCart[existingIndex].base_price = item.b2b_price;
      setCart(newCart);
    } else {
      const tieredPrice = calculateTieredPrice(item.b2b_price, 1, item.price_tiers);
      setCart([...cart, {
        item_id: item.id,
        item_type: type,
        item_name: item.name,
        vendor_id: item.vendor_id,
        vendor_name: item.vendor_name,
        vendor_gst_percentage: item.vendor_gst_percentage ?? 18,
        quantity: 1,
        unit_price: tieredPrice,
        total_price: tieredPrice,
        price_tiers: item.price_tiers,
        base_price: item.b2b_price,
      }]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    const newQty = newCart[index].quantity + delta;
    if (newQty <= 0) {
      newCart.splice(index, 1);
    } else {
      const tieredPrice = calculateTieredPrice(
        newCart[index].base_price || newCart[index].unit_price, 
        newQty, 
        newCart[index].price_tiers
      );
      newCart[index].quantity = newQty;
      newCart[index].unit_price = tieredPrice;
      newCart[index].total_price = newQty * tieredPrice;
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const getCartByVendor = () => {
    const grouped = {};
    cart.forEach(item => {
      if (!grouped[item.vendor_id]) {
        grouped[item.vendor_id] = { vendor_id: item.vendor_id, vendor_name: item.vendor_name, vendor_gst_percentage: item.vendor_gst_percentage ?? 18, items: [] };
      }
      grouped[item.vendor_id].items.push(item);
    });
    return Object.values(grouped);
  };

  const selectPatient = (patient) => {
    setOrderDetails({
      ...orderDetails,
      patient_id: String(patient.id || ''),
      patient_name: String(patient.name || ''),
      patient_phone: String(patient.phone || ''),
      patient_email: String(patient.email || ''),
      patient_age: String(patient.age || ''),
      patient_gender: String(patient.gender || ''),
    });
    setShowPatientSelect(false);
  };

  const placeOrder = async (vendorCart) => {
    // Calculate cart total for this vendor
    const vendorTotal = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
    
    try {
      const appliedCoupon = appliedCoupons[vendorCart.vendor_id];
      const orderData = {
        vendor_id: vendorCart.vendor_id,
        items: vendorCart.items.map(i => ({
          item_id: i.item_id, item_type: i.item_type, item_name: i.item_name,
          quantity: i.quantity, unit_price: i.unit_price, total_price: i.total_price,
        })),
        patient_id: orderDetails.patient_id || null,
        patient_name: orderDetails.patient_name || '',
        patient_phone: orderDetails.patient_phone || '',
        patient_email: orderDetails.patient_email || '',
        patient_age: orderDetails.patient_age ? String(orderDetails.patient_age) : '',
        patient_gender: orderDetails.patient_gender || '',
        delivery_address: orderDetails.delivery_address || '',
        delivery_phone: orderDetails.delivery_phone || '',
        delivery_location: orderDetails.delivery_location || null,
        notes: orderDetails.notes || '',
        discount_type: orderDetails.discount_type || null,
        discount_value: orderDetails.discount_value || 0,
        gst_percent: vendorCart.vendor_gst_percentage ?? 18,
        apply_gst: orderDetails.apply_gst !== false,
        // Include coupon info if applied
        coupon_code: appliedCoupon?.code || null,
        coupon_discount: appliedCoupon?.calculated_discount || 0,
        payment_method: orderDetails.payment_method || 'wallet',
      };
      await B2BAPI.post('/orders', orderData);
      const method = orderDetails.payment_method === 'credit' ? 'on credit' : 'from wallet';
      toast.success(`Order placed with ${vendorCart.vendor_name}. ₹${vendorTotal.toLocaleString()} ${method}.`);
      setCart(cart.filter(c => c.vendor_id !== vendorCart.vendor_id));
      // Remove applied coupon for this vendor
      const newCoupons = { ...appliedCoupons };
      delete newCoupons[vendorCart.vendor_id];
      setAppliedCoupons(newCoupons);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(d => d.msg || d).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to place order');
      toast.error(errorMsg);
      throw err; // Re-throw to stop placeAllOrders
    }
  };

  const placeAllOrders = async () => {
    // Calculate final total after coupon discounts
    const finalTotal = cartTotal;  // cartTotal already includes coupon discount
    
    if (orderDetails.payment_method !== 'credit' && finalTotal > walletBalance) {
      toast.error(`Insufficient wallet balance. Required: ₹${finalTotal.toLocaleString()}, Available: ₹${walletBalance.toLocaleString()}`);
      return;
    }
    
    setPlacingOrder(true);
    try {
      for (const vc of getCartByVendor()) {
        await placeOrder(vc);
      }
      setShowOrderDialog(false);
      setOrderDetails({
        patient_id: '', patient_name: '', patient_phone: '', patient_email: '',
        patient_age: '', patient_gender: '', delivery_address: '', delivery_phone: '',
        delivery_location: null, notes: '',
        discount_type: 'none', discount_value: 0, gst_percent: 18, apply_gst: true
      });
      // Refresh wallet balance
      fetchData();
    } catch (err) {
      // Error already shown by placeOrder
    } finally {
      setPlacingOrder(false);
    }
  };

  const updateOrder = async () => {
    if (!editingOrder) return;
    try {
      await B2BAPI.put(`/orders/my-orders/${editingOrder.id}`, {
        patient_name: editingOrder.patient_name,
        patient_phone: editingOrder.patient_phone,
        delivery_address: editingOrder.delivery_address,
        notes: editingOrder.notes,
        discount_type: editingOrder.discount_type,
        discount_value: editingOrder.discount_value,
        gst_percent: editingOrder.gst_percent,
        apply_gst: editingOrder.apply_gst,
      });
      toast.success('Order updated');
      setEditingOrder(null);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order');
      toast.error(errorMsg);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await B2BAPI.put(`/orders/my-orders/${orderId}/cancel`);
      toast.success('Order cancelled');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed to cancel');
      toast.error(errorMsg);
    }
  };

  const downloadReport = async (report) => {
    try {
      const res = await B2BAPI.get(`/reports/download/${report.id}`);
      const link = document.createElement('a');
      link.href = `data:${res.data.content_type};base64,${res.data.content}`;
      link.download = res.data.filename;
      link.click();
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const shareOrderWhatsApp = (order) => {
    const items = order.items?.map(i => `• ${i.item_name} x${i.quantity} - ₹${i.total_price}`).join('\n') || '';
    const msg = `*Order Details*\nOrder #: ${order.order_number}\nVendor: ${order.vendor_name}\n\n*Items:*\n${items}\n\nSubtotal: ₹${order.subtotal}\nGST: ₹${order.tax}\n*Total: ₹${order.total}*\n\nPatient: ${order.patient_name || 'N/A'}\nStatus: ${order.status}`;
    // Use vendor phone if available, otherwise open blank WhatsApp
    const vendorPhone = order.vendor_phone ? order.vendor_phone.replace(/[^0-9]/g, '') : '';
    const waUrl = vendorPhone ? `https://wa.me/${vendorPhone.startsWith('91') ? vendorPhone : '91' + vendorPhone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');
  };

  // Apply coupon to a vendor's cart
  const applyCoupon = async (vendorId, code) => {
    if (!code.trim()) return;
    setValidatingCoupon(true);
    try {
      const vendorCart = getCartByVendor().find(vc => vc.vendor_id === vendorId);
      if (!vendorCart) {
        toast.error('No items from this vendor in cart');
        return;
      }
      
      const orderValue = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
      const res = await B2BAPI.post(`/coupons/validate?vendor_id=${vendorId}&code=${code.toUpperCase()}&order_value=${orderValue}`);
      
      if (res.data.valid) {
        setAppliedCoupons({
          ...appliedCoupons,
          [vendorId]: {
            code: res.data.code,
            discount_type: res.data.discount_type,
            discount_value: res.data.discount_value,
            calculated_discount: res.data.calculated_discount,
            description: res.data.description
          }
        });
        toast.success(`Coupon ${res.data.code} applied! Save ₹${res.data.calculated_discount}`);
        setCouponCode('');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Invalid coupon');
      toast.error(errorMsg);
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Remove applied coupon for a vendor
  const removeCoupon = (vendorId) => {
    const newCoupons = { ...appliedCoupons };
    delete newCoupons[vendorId];
    setAppliedCoupons(newCoupons);
    toast.info('Coupon removed');
  };

  // Get coupons available for vendors in the cart
  const getCouponsForVendor = (vendorId) => {
    return availableCoupons.filter(c => c.vendor_id === vendorId);
  };

  // Calculate total discount from coupons
  const getTotalCouponDiscount = () => {
    return Object.values(appliedCoupons).reduce((sum, c) => sum + (c.calculated_discount || 0), 0);
  };

  const downloadInvoice = (order) => {
    const invoiceContent = `
      <html><head><title>Invoice ${order.order_number}</title>
      <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}.total{font-weight:bold;font-size:18px}</style></head>
      <body><h1>Invoice</h1><p><strong>Order #:</strong> ${order.order_number}</p><p><strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString()}</p>
      <p><strong>Vendor:</strong> ${order.vendor_name}</p><p><strong>Patient:</strong> ${order.patient_name || 'N/A'}</p>
      <table><tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr>
      ${order.items?.map(i => `<tr><td>${i.item_name}</td><td>${i.quantity}</td><td>₹${i.unit_price}</td><td>₹${i.total_price}</td></tr>`).join('')}
      </table><br><p>Subtotal: ₹${order.subtotal}</p>
      ${order.discount_amount > 0 ? `<p>Discount: -₹${order.discount_amount}</p>` : ''}
      <p>GST (${order.gst_percent || 18}%): ₹${order.tax}</p><p class="total">Total: ₹${order.total}</p></body></html>
    `;
    const blob = new Blob([invoiceContent], { type: 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Invoice_${order.order_number}.html`;
    link.click();
  };

  // Calculations - Include coupon discount in final total
  const cartSubtotal = cart.reduce((sum, item) => sum + item.total_price, 0);
  const couponDiscount = getTotalCouponDiscount();
  const afterCouponDiscount = cartSubtotal - couponDiscount;
  // Calculate GST per vendor group
  const cartTax = orderDetails.apply_gst ? getCartByVendor().reduce((sum, vc) => {
    const vcSubtotal = vc.items.reduce((s, i) => s + i.total_price, 0);
    const vcCouponDiscount = appliedCoupons[vc.vendor_id]?.discount_amount || 0;
    return sum + Math.round((vcSubtotal - vcCouponDiscount) * (vc.vendor_gst_percentage ?? 18) / 100);
  }, 0) : 0;
  const cartTotal = afterCouponDiscount + cartTax;  // This is the final amount to pay

  const filterItems = (items) => {
    if (!searchQuery) return items;
    return items.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.generic_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Mask raw dev/test seed strings so the marketplace looks retail-ready
  const cleanProductName = (name, fallback = 'Healthcare Product') => {
    if (!name || /^test[_\s]/i.test(name)) return fallback;
    return name;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  // Filter orders by type
  const labOrders = orders.filter(o => o.vendor_type === 'lab');
  const pharmaOrders = orders.filter(o => o.vendor_type === 'pharma');
  const equipmentOrders = orders.filter(o => o.vendor_type === 'inventory');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="marketplace-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">B2B Marketplace</h1>
          <p className="text-slate-500 text-sm">Order lab tests, medicines, and equipment at B2B prices</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products or vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="marketplace-search"
            />
          </div>
          <Button variant="outline" className="relative" onClick={() => setShowCart(true)} data-testid="cart-btn">
            <ShoppingCart className="w-5 h-5" />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-teal-600 text-white text-xs rounded-full flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile-friendly Tab Navigation */}
      <MobileTabNav
        tabs={[
          { id: 'lab-tests', label: 'Lab Tests', icon: FlaskConical },
          { id: 'medicines', label: 'Medicines', icon: Pill },
          { id: 'equipment', label: 'Equipment', icon: Package },
          { id: 'coupons', label: 'Coupons', icon: Tag, badge: availableCoupons.length > 0 ? availableCoupons.length : null },
          { id: 'orders', label: 'Orders', icon: FileText },
          { id: 'reports', label: 'Reports', icon: Download },
          { id: 'my-vendors', label: 'Vendors', icon: Users, onClick: fetchMyVendors },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="hidden">
          <TabsTrigger value="lab-tests">Lab Tests</TabsTrigger>
          <TabsTrigger value="medicines">Medicines</TabsTrigger>
          <TabsTrigger value="equipment">Equipment</TabsTrigger>
          <TabsTrigger value="coupons">Coupons</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="my-vendors">Vendors</TabsTrigger>
        </TabsList>

        {/* Lab Tests with Price Comparison */}
        <TabsContent value="lab-tests">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterItems(labTests).map((test) => (
              <Card key={test.id} className="hover:shadow-md transition-shadow" data-testid={`lab-test-${test.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                        <FlaskConical className="w-5 h-5 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 text-sm">{cleanProductName(test.name, 'Diagnostic Test')}</h3>
                        <p className="text-xs text-slate-500">{test.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {test.home_collection && <Badge variant="outline" className="text-xs"><Home className="w-3 h-3 mr-1" />Home</Badge>}
                      {test.vendor_is_open === false && <Badge variant="outline" className="text-xs text-red-500 border-red-300">Closed</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-2 line-clamp-2">{test.description}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Building2 className="w-3 h-3" /> {test.vendor_name}
                    <span className="text-slate-300">|</span>
                    <Clock className="w-3 h-3" /> {test.turnaround_time}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 line-through">₹{test.mrp}</span>
                      <span className="text-lg font-bold text-green-600 ml-2">₹{test.b2b_price}</span>
                      <span className="text-xs text-green-600 ml-1">({Math.round((1 - test.b2b_price/test.mrp) * 100)}% off)</span>
                    </div>
                    <Button size="sm" onClick={() => addToCart(test, 'lab_test')}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <ReviewSection productId={test.id} vendorId={test.vendor_id} productType="lab_test" canReview={true} token={localStorage.getItem('clinic_token')} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Medicines with Price Comparison */}
        <TabsContent value="medicines">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterItems(medicines).map((med) => (
              <Card key={med.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  {med.photo_url && (
                    <div 
                      className="mb-3 h-32 bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxPhoto({ url: `${process.env.REACT_APP_BACKEND_URL}${med.photo_url}`, name: med.name })}
                    >
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${med.photo_url}`} alt={med.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {!med.photo_url && (
                        <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                          <Pill className="w-5 h-5 text-pink-600" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800 text-sm">{cleanProductName(med.name, 'Pharma Product')}</h3>
                        <p className="text-xs text-slate-500">{med.generic_name || med.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {med.prescription_required && <Badge variant="outline" className="text-xs bg-red-50 text-red-600">Rx</Badge>}
                      {med.vendor_is_open === false && <Badge variant="outline" className="text-xs text-red-500 border-red-300">Closed</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">{med.manufacturer} • {med.pack_size}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Building2 className="w-3 h-3" /> {med.vendor_name}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 line-through">₹{med.mrp}</span>
                      <span className="text-lg font-bold text-green-600 ml-2">₹{med.b2b_price}</span>
                      {med.price_tiers && Object.keys(med.price_tiers).length > 0 && (
                        <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">Volume Discounts</span>
                      )}
                    </div>
                    <Button size="sm" onClick={() => addToCart(med, 'medicine')}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {med.price_tiers && Object.keys(med.price_tiers).length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-1">Volume Pricing:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(med.price_tiers).map(([range, price]) => (
                          <Badge key={range} variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            {range}: ₹{price}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t">
                    <ReviewSection productId={med.id} vendorId={med.vendor_id} productType="medicine" canReview={true} token={localStorage.getItem('clinic_token')} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Equipment */}
        <TabsContent value="equipment">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterItems(inventory).map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  {item.photo_url && (
                    <div 
                      className="mb-3 h-32 bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxPhoto({ url: `${process.env.REACT_APP_BACKEND_URL}${item.photo_url}`, name: item.name })}
                    >
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${item.photo_url}`} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {!item.photo_url && (
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-orange-600" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800 text-sm">{cleanProductName(item.name, 'Medical Supply')}</h3>
                        <p className="text-xs text-slate-500">{item.brand} {item.model_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {item.warranty_months > 0 && <Badge variant="outline" className="text-xs">{item.warranty_months}m warranty</Badge>}
                      {item.vendor_is_open === false && <Badge variant="outline" className="text-xs text-red-500 border-red-300">Closed</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">{item.description}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Building2 className="w-3 h-3" /> {item.vendor_name}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400 line-through">₹{item.mrp}</span>
                      <span className="text-lg font-bold text-green-600 ml-2">₹{item.b2b_price}</span>
                      {item.price_tiers && Object.keys(item.price_tiers).length > 0 && (
                        <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">Volume Discounts</span>
                      )}
                    </div>
                    <Button size="sm" onClick={() => addToCart(item, 'inventory_product')}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {item.price_tiers && Object.keys(item.price_tiers).length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-1">Volume Pricing:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(item.price_tiers).map(([range, price]) => (
                          <Badge key={range} variant="outline" className="text-xs bg-purple-50 text-purple-700">
                            {range}: ₹{price}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t">
                    <ReviewSection productId={item.id} vendorId={item.vendor_id} productType="inventory_product" canReview={true} token={localStorage.getItem('clinic_token')} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Coupons Tab */}
        <TabsContent value="coupons">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-600" />
                Available Coupons & Discounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {availableCoupons.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No active coupons available</p>
                  <p className="text-sm text-slate-400 mt-1">Check back later for vendor promotions</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableCoupons.map((coupon) => (
                    <div key={coupon.id} className="border border-dashed border-green-300 rounded-lg p-4 bg-green-50/30 hover:bg-green-50 transition-colors" data-testid={`coupon-${coupon.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-bold text-lg text-green-700">{coupon.code}</p>
                          <p className="text-xs text-slate-500">{coupon.vendor_name}</p>
                        </div>
                        <Badge className={`text-xs ${coupon.vendor_type === 'lab' ? 'bg-cyan-100 text-cyan-700' : coupon.vendor_type === 'pharma' ? 'bg-pink-100 text-pink-700' : 'bg-orange-100 text-orange-700'}`}>
                          {coupon.vendor_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{coupon.description || 'Get discount on your order'}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-green-600">
                          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `₹${coupon.discount_value} OFF`}
                        </div>
                      </div>
                      {coupon.min_order_value > 0 && (
                        <p className="text-xs text-slate-400 mt-2">Min. order: ₹{coupon.min_order_value}</p>
                      )}
                      {coupon.valid_until && (
                        <p className="text-xs text-amber-600 mt-1">Valid till: {new Date(coupon.valid_until).toLocaleDateString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Orders with Filters and Tabs */}
        <TabsContent value="orders">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Search</Label>
                  <Input
                    placeholder="Order #, vendor, patient..."
                    value={orderFilters.search}
                    onChange={(e) => setOrderFilters({ ...orderFilters, search: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={orderFilters.vendor_type} onValueChange={(v) => setOrderFilters({ ...orderFilters, vendor_type: v })}>
                    <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="lab">Lab Tests</SelectItem>
                      <SelectItem value="pharma">Medicines</SelectItem>
                      <SelectItem value="inventory">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={orderFilters.status} onValueChange={(v) => setOrderFilters({ ...orderFilters, status: v })}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={orderFilters.date_from} onChange={(e) => setOrderFilters({ ...orderFilters, date_from: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={orderFilters.date_to} onChange={(e) => setOrderFilters({ ...orderFilters, date_to: e.target.value })} />
                </div>
                <div className="sm:col-span-2 md:col-span-3 lg:col-span-6">
                  <Button className="w-full sm:w-auto" onClick={fetchFilteredOrders}><Filter className="w-4 h-4 mr-1" /> Filter</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="all">
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="all">All ({orders.length})</TabsTrigger>
              <TabsTrigger value="lab">Lab Tests ({labOrders.length})</TabsTrigger>
              <TabsTrigger value="pharma">Medicines ({pharmaOrders.length})</TabsTrigger>
              <TabsTrigger value="equipment">Equipment ({equipmentOrders.length})</TabsTrigger>
            </TabsList>

            {['all', 'lab', 'pharma', 'equipment'].map((type) => {
              const typeOrders = type === 'all' ? orders : type === 'lab' ? labOrders : type === 'pharma' ? pharmaOrders : equipmentOrders;
              return (
                <TabsContent key={type} value={type}>
                  <div className="space-y-4">
                    {typeOrders.length === 0 ? (
                      <Card><CardContent className="py-8 text-center text-slate-500">No orders found</CardContent></Card>
                    ) : typeOrders.map((order) => (
                      <Card key={order.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-mono text-sm text-slate-500">{order.order_number}</p>
                              <p className="font-semibold">{order.vendor_name}</p>
                              {order.patient_name && <p className="text-sm text-slate-500">Patient: {order.patient_name}</p>}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">₹{order.total?.toLocaleString()}</p>
                              {getStatusBadge(order.status)}
                              {order.payment_method === 'credit' && (
                                <Badge className={`mt-1 block ${
                                  order.credit_status === 'paid' ? 'bg-green-100 text-green-700' :
                                  order.credit_status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }`} data-testid={`credit-badge-${order.id}`}>
                                  {order.credit_status === 'paid' ? 'Credit Paid' : order.credit_status === 'overdue' ? 'Overdue' : 'On Credit'}
                                </Badge>
                              )}
                              {order.order_type === 'subscription' && (
                                <Badge className="mt-1 block bg-purple-100 text-purple-700">
                                  {order.subscription_period || 'Subscription'}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-3 mb-3">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm py-1">
                                <span>{item.item_name} x{item.quantity}</span>
                                <span>₹{item.total_price}</span>
                              </div>
                            ))}
                            <div className="border-t mt-2 pt-2 text-sm">
                              <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
                              {order.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-₹{order.discount_amount}</span></div>}
                              <div className="flex justify-between text-slate-500"><span>GST ({order.gst_percent || 18}%)</span><span>₹{order.tax}</span></div>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString()}</p>
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => shareOrderWhatsApp(order)}>
                                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => downloadInvoice(order)}>
                                <Download className="w-4 h-4 mr-1" /> Invoice
                              </Button>
                              {['pending', 'confirmed'].includes(order.status) && (
                                <Button size="sm" variant="outline" onClick={() => setEditingOrder(order)}>
                                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                                </Button>
                              )}
                              {order.status === 'pending' && (
                                <Button size="sm" variant="destructive" onClick={() => cancelOrder(order.id)}>Cancel</Button>
                              )}
                              {order.payment_method === 'credit' && order.credit_status !== 'paid' && (
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                                  if (!window.confirm(`Pay ₹${(order.total - (order.credit_amount_paid || 0)).toLocaleString()} from wallet for this credit order?`)) return;
                                  try {
                                    await B2BAPI.post(`/orders/my-orders/${order.id}/pay-credit`);
                                    toast.success('Credit payment processed!');
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Payment failed');
                                  }
                                }} data-testid={`pay-credit-${order.id}`}>
                                  <IndianRupee className="w-3 h-3 mr-1" /> Pay Now
                                </Button>
                              )}
                            </div>
                          </div>
                          <InvoiceUploader orderId={order.id} orderType="b2b" canUpload={false} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* Lab Reports */}
        <TabsContent value="reports">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs">Patient Name</Label>
                  <Input
                    placeholder="Search patient..."
                    value={reportFilters.patient_name}
                    onChange={(e) => setReportFilters({ ...reportFilters, patient_name: e.target.value })}
                  />
                </div>
                <div className="w-48">
                  <Label className="text-xs">Lab</Label>
                  <Select value={reportFilters.vendor_id} onValueChange={(v) => setReportFilters({ ...reportFilters, vendor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="All Labs" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Labs</SelectItem>
                      {labs.map(lab => <SelectItem key={lab.id} value={lab.id}>{lab.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-36">
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={reportFilters.date_from} onChange={(e) => setReportFilters({ ...reportFilters, date_from: e.target.value })} />
                </div>
                <div className="w-36">
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={reportFilters.date_to} onChange={(e) => setReportFilters({ ...reportFilters, date_to: e.target.value })} />
                </div>
                <Button onClick={fetchFilteredReports}><Filter className="w-4 h-4 mr-1" /> Filter</Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {reports.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">No reports available</CardContent></Card>
            ) : reports.map((report) => (
              <Card key={report.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{report.report_title}</h3>
                      <p className="text-sm text-slate-500">Patient: {report.patient_name}</p>
                      <p className="text-xs text-slate-400">{report.vendor_name} • {new Date(report.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Button onClick={() => downloadReport(report)}>
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* My Vendors Tab */}
        <TabsContent value="my-vendors">
          <div className="space-y-6">
            {/* Doctor Link Vendors - Search & Incoming Requests */}
            <DoctorLinkVendors token={localStorage.getItem('clinic_token')} onRefresh={fetchMyVendors} />

            {/* Connected Vendors */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-green-600" /> My Connected Vendors
                    </CardTitle>
                    <CardDescription>View and manage your vendor connections</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {vendorsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : myVendors.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No connected vendors yet. Search and connect above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myVendors.map((mapping) => (
                      <div 
                        key={mapping.id} 
                        className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                        data-testid={`vendor-connection-${mapping.vendor_id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100">
                            {getVendorIcon(mapping.vendor_type)}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-800">{mapping.vendor_name}</h3>
                            <Badge variant="secondary" className="capitalize mt-1">{mapping.vendor_type}</Badge>
                            <div className="mt-3 space-y-1 text-sm text-slate-600">
                              {mapping.vendor_email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4" />
                                  <span>{mapping.vendor_email}</span>
                                </div>
                              )}
                              {mapping.vendor_phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4" />
                                  <span>{mapping.vendor_phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge className="bg-green-100 text-green-700">Connected</Badge>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-500 border-red-200 hover:bg-red-50 text-xs"
                              onClick={async () => {
                                if (!window.confirm('Remove this vendor connection?')) return;
                                try {
                                  const t = localStorage.getItem('clinic_token');
                                  await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-doctor-mappings/doctor/unlink/${mapping.id}`, { headers: { Authorization: `Bearer ${t}` }});
                                  toast.success('Connection removed');
                                  fetchMyVendors();
                                } catch (err) { toast.error('Failed to remove connection'); }
                              }}
                              data-testid={`unlink-vendor-btn-${mapping.vendor_id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setShowCart(false)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">Shopping Cart ({cart.length})</h2>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 h-[calc(100vh-280px)]">
              {cart.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getCartByVendor().map((vc) => (
                    <div key={vc.vendor_id} className="border rounded-lg p-3">
                      <p className="font-medium text-sm text-slate-600 mb-2"><Building2 className="w-4 h-4 inline mr-1" />{vc.vendor_name}</p>
                      {vc.items.map((item) => {
                        const idx = cart.findIndex(c => c.item_id === item.item_id);
                        const hasVolumeDiscount = item.price_tiers && Object.keys(item.price_tiers).length > 0;
                        const isDiscounted = hasVolumeDiscount && item.unit_price < item.base_price;
                        return (
                          <div key={item.item_id} className="flex items-center justify-between py-2 border-t">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.item_name}</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-slate-500">₹{item.unit_price} each</p>
                                {isDiscounted && (
                                  <Badge className="text-[10px] bg-purple-100 text-purple-700 px-1 py-0">
                                    Volume Discount Applied!
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateCartQuantity(idx, -1)} className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <button onClick={() => updateCartQuantity(idx, 1)} className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                              <button onClick={() => removeFromCart(idx)} className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center ml-2"><X className="w-3 h-3" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 border-t bg-white p-4">
                <div className="space-y-1 mb-3 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{cartSubtotal.toLocaleString()}</span></div>
                  {couponDiscount > 0 && <div className="flex justify-between text-green-600"><span>Coupon Discount</span><span>-₹{couponDiscount.toLocaleString()}</span></div>}
                  <div className="flex justify-between text-slate-500"><span>GST {getCartByVendor().length === 1 ? `(${getCartByVendor()[0].vendor_gst_percentage ?? 18}%)` : ''}</span><span>₹{cartTax.toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold text-lg pt-1 border-t"><span>Total</span><span>₹{cartTotal.toLocaleString()}</span></div>
                </div>
                
                {/* Wallet Balance Info */}
                <div className="mb-3 p-2 rounded-lg bg-slate-50 border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-teal-600" />
                    <span className="text-sm text-slate-600">Wallet: ₹{walletBalance.toLocaleString()}</span>
                  </div>
                  <Button variant="link" size="sm" className="text-teal-600 h-auto p-0" onClick={() => window.location.href = '/doctor/wallet'}>
                    Add Funds
                  </Button>
                </div>
                
                <Button className="w-full" onClick={() => { setShowCart(false); setShowOrderDialog(true); }}>
                  Proceed to Checkout
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Complete Your Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Patient Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Patient Details</Label>
                <Button size="sm" variant="outline" onClick={() => setShowPatientSelect(true)}>
                  <User className="w-4 h-4 mr-1" /> Select Patient
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Patient Name" value={orderDetails.patient_name} onChange={(e) => setOrderDetails({ ...orderDetails, patient_name: e.target.value })} />
                <Input placeholder="Phone" value={orderDetails.patient_phone} onChange={(e) => setOrderDetails({ ...orderDetails, patient_phone: e.target.value })} />
                <Input placeholder="Email" value={orderDetails.patient_email} onChange={(e) => setOrderDetails({ ...orderDetails, patient_email: e.target.value })} />
                <div className="flex gap-2">
                  <Input placeholder="Age" value={orderDetails.patient_age} onChange={(e) => setOrderDetails({ ...orderDetails, patient_age: e.target.value })} className="w-20" />
                  <Select value={orderDetails.patient_gender} onValueChange={(v) => setOrderDetails({ ...orderDetails, patient_gender: v })}>
                    <SelectTrigger><SelectValue placeholder="Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Delivery Address & Phone</Label>
              <AddressPickerCheckout
                token={localStorage.getItem('token')}
                initialAddress={orderDetails.delivery_address}
                initialPhone={orderDetails.delivery_phone}
                initialLocation={orderDetails.delivery_location}
                onAddressSelected={(data) => {
                  setOrderDetails(od => ({
                    ...od,
                    delivery_address: data.address || '',
                    delivery_phone: data.phone || '',
                    delivery_location: data.location || null,
                  }));
                }}
              />
            </div>

            {/* Apply Coupon Section */}
            <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <Label className="text-green-700 flex items-center gap-2"><Tag className="w-4 h-4" /> Apply Vendor Coupons</Label>
              
              {getCartByVendor().map((vendorCart) => {
                const vendorCoupons = getCouponsForVendor(vendorCart.vendor_id);
                const appliedCoupon = appliedCoupons[vendorCart.vendor_id];
                const vendorSubtotal = vendorCart.items.reduce((sum, i) => sum + i.total_price, 0);
                
                return (
                  <div key={vendorCart.vendor_id} className="bg-white rounded-lg p-3 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{vendorCart.vendor_name}</span>
                      <span className="text-xs text-slate-500">Subtotal: ₹{vendorSubtotal}</span>
                    </div>
                    
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between bg-green-100 rounded p-2">
                        <div>
                          <span className="font-bold text-green-700">{appliedCoupon.code}</span>
                          <span className="text-sm text-green-600 ml-2">-₹{appliedCoupon.calculated_discount}</span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 text-red-500" onClick={() => removeCoupon(vendorCart.vendor_id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input 
                            placeholder="Enter coupon code" 
                            className="flex-1 h-8 text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && applyCoupon(vendorCart.vendor_id, e.target.value)}
                          />
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8"
                            disabled={validatingCoupon}
                            onClick={(e) => {
                              const input = e.target.closest('.space-y-2').querySelector('input');
                              applyCoupon(vendorCart.vendor_id, input.value);
                            }}
                          >
                            Apply
                          </Button>
                        </div>
                        
                        {vendorCoupons.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {vendorCoupons.slice(0, 3).map((c) => (
                              <button
                                key={c.id}
                                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
                                onClick={() => applyCoupon(vendorCart.vendor_id, c.code)}
                              >
                                {c.code}: {c.discount_type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`} off
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* GST Section */}
            <div className="space-y-2 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-blue-700">GST Settings</Label>
                <div className="flex items-center gap-2">
                  <Switch checked={orderDetails.apply_gst} onCheckedChange={(v) => setOrderDetails({ ...orderDetails, apply_gst: v })} />
                  <span className="text-sm">{orderDetails.apply_gst ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              {orderDetails.apply_gst && (
                <div className="space-y-1">
                  {getCartByVendor().map(vc => (
                    <div key={vc.vendor_id} className="flex items-center gap-2 text-sm text-blue-700">
                      <span>{vc.vendor_name}:</span>
                      <span className="font-semibold">{vc.vendor_gst_percentage ?? 18}% GST</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Textarea placeholder="Notes" value={orderDetails.notes} onChange={(e) => setOrderDetails({ ...orderDetails, notes: e.target.value })} />

            {/* Order Summary */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium mb-2">Order Summary</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>{cart.length} items</span><span>₹{cartSubtotal.toLocaleString()}</span></div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Coupon Discount</span>
                    <span>-₹{couponDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-500"><span>GST</span><span>₹{cartTax.toLocaleString()}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t"><span>Total to Pay</span><span>₹{cartTotal.toLocaleString()}</span></div>
              </div>
            </div>
            
            {/* Payment Method Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${orderDetails.payment_method === 'wallet' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setOrderDetails({ ...orderDetails, payment_method: 'wallet' })}
                  data-testid="payment-wallet-btn"
                >
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-medium text-sm">Pay via Wallet</p>
                      <p className="text-xs text-slate-500">Balance: ₹{walletBalance.toLocaleString()}</p>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${orderDetails.payment_method === 'credit' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setOrderDetails({ ...orderDetails, payment_method: 'credit' })}
                  data-testid="payment-credit-btn"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-sm">Buy on Credit</p>
                      <p className="text-xs text-slate-500">Pay later (vendor terms)</p>
                    </div>
                  </div>
                </button>
              </div>
              {orderDetails.payment_method === 'wallet' && cartTotal > walletBalance && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-red-600">Insufficient wallet balance</p>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-300 h-7 text-xs" onClick={() => window.location.href = '/doctor/wallet'}>Add Funds</Button>
                </div>
              )}
              {orderDetails.payment_method === 'credit' && (
                <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                  Amount will be auto-debited from your wallet after the vendor's credit period. If insufficient funds, portal access will be restricted.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>Cancel</Button>
            <Button 
              onClick={placeAllOrders} 
              disabled={placingOrder || (orderDetails.payment_method === 'wallet' && cartTotal > walletBalance)}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {placingOrder ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Placing Order...</>
              ) : orderDetails.payment_method === 'credit' ? (
                <><FileText className="w-4 h-4 mr-2" /> Order on Credit ₹{cartTotal.toLocaleString()}</>
              ) : cartTotal > walletBalance ? (
                <><Lock className="w-4 h-4 mr-2" /> Add Funds First</>
              ) : (
                <><Wallet className="w-4 h-4 mr-2" /> Pay ₹{cartTotal.toLocaleString()}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Patient Selection Dialog */}
      <Dialog open={showPatientSelect} onOpenChange={setShowPatientSelect}>
        <DialogContent>
          <DialogHeader><DialogTitle>Select Patient</DialogTitle></DialogHeader>
          <Input placeholder="Search patients..." className="mb-4" />
          <div className="max-h-60 overflow-y-auto space-y-2">
            {patients.map((patient) => (
              <div key={patient.id} className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => selectPatient(patient)}>
                <p className="font-medium">{patient.name}</p>
                <p className="text-sm text-slate-500">{patient.phone} • {patient.age} • {patient.gender}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!editingOrder} onOpenChange={(v) => !v && setEditingOrder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Order</DialogTitle></DialogHeader>
          {editingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Patient Name</Label><Input value={editingOrder.patient_name || ''} onChange={(e) => setEditingOrder({ ...editingOrder, patient_name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={editingOrder.patient_phone || ''} onChange={(e) => setEditingOrder({ ...editingOrder, patient_phone: e.target.value })} /></div>
              </div>
              <div><Label>Delivery Address</Label><Textarea value={editingOrder.delivery_address || ''} onChange={(e) => setEditingOrder({ ...editingOrder, delivery_address: e.target.value })} /></div>
              <div className="flex gap-2">
                <Select value={editingOrder.discount_type || 'none'} onValueChange={(v) => setEditingOrder({ ...editingOrder, discount_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Discount Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Discount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="amount">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Discount" value={editingOrder.discount_value || 0} onChange={(e) => setEditingOrder({ ...editingOrder, discount_value: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center gap-4">
                <Switch checked={editingOrder.apply_gst !== false} onCheckedChange={(v) => setEditingOrder({ ...editingOrder, apply_gst: v })} />
                <span>Apply GST</span>
                {editingOrder.apply_gst !== false && <Input type="number" value={editingOrder.gst_percent || 18} onChange={(e) => setEditingOrder({ ...editingOrder, gst_percent: parseFloat(e.target.value) || 0 })} className="w-20" />}
              </div>
              <Textarea placeholder="Notes" value={editingOrder.notes || ''} onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingOrder(null)}>Cancel</Button>
            <Button onClick={updateOrder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Vendor Connection Dialog */}
      <Dialog open={showRequestVendorDialog} onOpenChange={setShowRequestVendorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Vendor Connection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select Vendor</Label>
              <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a vendor to connect with..." />
                </SelectTrigger>
                <SelectContent>
                  {availableVendors.filter(v => v.connection_status === 'not_connected').map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      <div className="flex items-center gap-2">
                        {v.name} <span className="text-slate-400 capitalize">({v.vendor_type})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableVendors.filter(v => v.connection_status === 'not_connected').length === 0 && (
                <p className="text-xs text-amber-600 mt-2">No new vendors available to connect with</p>
              )}
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={requestVendorNotes}
                onChange={(e) => setRequestVendorNotes(e.target.value)}
                placeholder="Any notes for the admin reviewing your request..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Your request will be sent to the platform admin for approval. Once approved, this vendor's products will be visible in the marketplace.
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowRequestVendorDialog(false); setSelectedVendorId(''); setRequestVendorNotes(''); }}>
                Cancel
              </Button>
              <Button onClick={requestVendorConnection} disabled={requestingVendor || !selectedVendorId}>
                {requestingVendor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Dialog */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-sm">{lightboxPhoto?.name}</DialogTitle>
          </DialogHeader>
          {lightboxPhoto && (
            <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
              <img 
                src={lightboxPhoto.url} 
                alt={lightboxPhoto.name} 
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
